import {
  toArray,
  unique,
  getMimeBase,
  getMimeTypes,
  isAudio,
  isImage,
  isText,
  isSVG,
  isHTML,
  isVideo,
} from '@nx-superprep/utils';
import { AssetType, Asset } from '../asset';
import { StorageReference } from '@angular/fire/storage';

/**
 * Represents a group of resources.
 */
export class ResourceGroup {
  /**
   * An array of resource names.
   */
  names: string[];

  /**
   * The MIME type of the resources.
   */
  mimeType: string;

  /**
   * The type of asset.
   */
  assetType: string;

  /**
   * Constructs a new ResourceGroup instance.
   * @param {Resource | ResourceGroup} options - An object containing optional properties for the resource group.
   */
  constructor(options: Resource | ResourceGroup) {
    this.names = unique(toArray('names' in options ? options.names : options.name));
    this.mimeType = options.mimeType;
    this.assetType = options.assetType ?? '';
  }

  /**
   * Retrieves an array of MIME types based on the main MIME type.
   * @returns {string[]} An array of MIME types.
   */
  get mimeTypes() {
    return getMimeTypes(this.mimeType);
  }
}

/**
 * Represents a map of resources.
 */
export class ResourceMap extends Map {
  /**
   * Adds a resource to the map.
   * @param {Resource} r - The resource to add.
   */
  add(r: Resource) {
    if (!r || !r.name || !r.mimeType || r.isText) {
      return;
    }
  }
}

/**
 * Represents a resource used by the card.
 */
export class Resource {
  /**
   * The key of the resource.
   */
  key!: string;

  /**
   * The name of the resource.
   */
  name!: string;

  /**
   * The reference to the storage.
   */
  reference?: StorageReference;

  /**
   * The asset associated with the resource.
   */
  asset?: Asset;

  /**
   * The ID of the asset.
   */
  assetId?: string;

  /**
   * The MIME type of the resource.
   */
  mimeType!: string;

  /**
   * The type of asset.
   */
  assetType?: string;

  /**
   * The HTML element associated with the resource.
   */
  htmlElement?: Partial<HTMLElement>;

  /**
   * The URL of the resource.
   */
  url = '';

  /**
   * The duration of the resource.
   */
  duration = 0;

  /**
   * Constructs a new Resource instance.
   * @param {Partial<Resource>} options - An object containing optional properties for the resource.
   */
  constructor(options: Partial<Resource>) {
    if (!options.name) {
      return;
    }
    this.key = options.key ?? '';
    this.name = options.name;
    this.mimeType = options.mimeType ?? '';
    this.assetType = options.assetType ?? this.getAssetType();
    if (options.htmlElement) {
      this.htmlElement = options.htmlElement;
    }
    if (options.asset) {
      if (typeof options.asset === 'string') {
        this.assetId = options.asset;
      } else {
        this.asset = new Asset(options.asset);
      }
    }
  }

  /**
   * Determines whether the resource should be assetized.
   * @returns {boolean} A boolean indicating whether the resource should be assetized.
   */
  get shouldAssetize(): boolean {
    return !this.isAssetized && (this.mimeBase.match(/^(svg|image|audio|video)/g) ?? []).length > 0;
  }

  /**
   * Determines whether the resource is assetized.
   * @returns {boolean} A boolean indicating whether the resource is assetized.
   */
  get isAssetized(): boolean {
    return this.mimeBase === 'text' || Boolean(this.url);
  }

  /**
   * Retrieves the base MIME type of the resource.
   * @returns {string} The base MIME type of the resource.
   */
  get mimeBase() {
    return getMimeBase(this.mimeType);
  }

  /**
   * Determines whether the resource is a video.
   * @returns {boolean} A boolean indicating whether the resource is a video.
   */
  get isVideo() {
    return isVideo(this.mimeType);
  }

  /**
   * Determines whether the resource is an audio.
   * @returns {boolean} A boolean indicating whether the resource is an audio.
   */
  get isAudio() {
    return isAudio(this.mimeType);
  }

  /**
   * Determines whether the resource is an image.
   * @returns {boolean} A boolean indicating whether the resource is an image.
   */
  get isImage() {
    return isImage(this.mimeType);
  }

  /**
   * Determines whether the resource is an SVG.
   * @returns {boolean} A boolean indicating whether the resource is an SVG.
   */
  get isSVG() {
    return isSVG(this.mimeType);
  }

  /**
   * Determines whether the resource is a text.
   * @returns {boolean} A boolean indicating whether the resource is a text.
   */
  get isText() {
    return isText(this.mimeType) && !this.isElement;
  }

  /**
   * Determines whether the resource is HTML.
   * @returns {boolean} A boolean indicating whether the resource is HTML.
   */
  get isHTML() {
    return isHTML(this.mimeType);
  }

  /**
   * Determines whether the resource is an HTML element.
   * @returns {boolean} A boolean indicating whether the resource is an HTML element.
   */
  get isElement() {
    return this.key === 'element';
  }

  /**
   * Retrieves the type of asset based on the MIME type of the resource.
   * @returns {AssetType} The type of asset.
   */
  getAssetType() {
    if (this.isSVG) {
      return AssetType.svg;
    }
    if (this.isImage) {
      return AssetType.image;
    }
    if (this.isAudio) {
      return AssetType.audio;
    }
    if (this.isVideo) {
      return AssetType.video;
    }
    if (this.isHTML || this.isText) {
      return AssetType.text;
    }
    return AssetType.unknown;
  }
}
